import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import {
  Brain,
  AlertOctagon,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Loader2,
  ChevronDown,
  ChevronUp,
  Flag,
  ThumbsUp,
  ThumbsDown,
  FileSearch,
  Layers,
  Sparkles,
  History,
  RefreshCw,
  Clock
} from 'lucide-react';
import { WorkflowCard } from '../shared/WorkflowCard';
import { PlaybookReviewPanel } from '../shared/PlaybookReviewPanel';
import { FinanceRuleReviewPanel } from '../shared/FinanceRuleReviewPanel';
import { ModelSelectionModal, SelectedModel, AIProvider } from '../shared/ModelSelectionModal';
import { DetailedClauseJSON } from '../../../../types/contract.types';
import { WorkflowContext, ClauseDecisionStatus } from '../../../../types/workflow.types';
import { AnalysisProgress } from '../../../../services/api/contractService';

interface AnalysisVersionInfo {
  _id: string;
  version: number;
  fileName: string;
  analysisDate: string;
  createdAt: string;
}

interface OutletContext extends WorkflowContext {
  onNextStep: () => void;
  onPrevStep: () => void;
  refreshAnalysis: () => Promise<void>;
  runAnalysis: (onProgress?: (progress: AnalysisProgress) => void, forceRefresh?: boolean, model?: string, provider?: AIProvider) => Promise<void>;
  analysisVersion: number;
  analysisVersions: AnalysisVersionInfo[];
  selectAnalysisVersion: (version: number) => Promise<void>;
}

export const Step2_Analysis: React.FC = () => {
  const context = useOutletContext<OutletContext>();
  const {
    analysis,
    clauseDecisions,
    isLoading,
    isSaving,
    runAnalysis,
    updateClauseDecision,
    analysisVersion,
    analysisVersions,
    selectAnalysisVersion
  } = context;

  const navigate = useNavigate();
  const { id: contractId } = useParams<{ id: string }>();

  const [expandedClauses, setExpandedClauses] = useState<Set<string>>(new Set());
  const [filterRisk, setFilterRisk] = useState<string>('all');
  const [analysisProgress, setAnalysisProgress] = useState<AnalysisProgress | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false);
  const [showModelSelection, setShowModelSelection] = useState(false);
  const [pendingReanalyze, setPendingReanalyze] = useState(false);

  // Timer state for tracking analysis duration
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Timer effect - runs when analysis is in progress
  useEffect(() => {
    if (isAnalyzing || isReanalyzing) {
      // Start the timer
      startTimeRef.current = Date.now();
      setElapsedTime(0);

      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);
    } else {
      // Stop the timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isAnalyzing, isReanalyzing]);

  // Format elapsed time as MM:SS
  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Navigation handlers - always use direct navigation for reliability
  const handleNextStep = useCallback(() => {
    if (contractId) {
      navigate(`/contracts/${contractId}/clauses`);
    }
  }, [contractId, navigate]);

  const handlePrevStep = useCallback(() => {
    if (contractId) {
      navigate(`/contracts/${contractId}/overview`);
    }
  }, [contractId, navigate]);

  const toggleClause = (clauseRef: string) => {
    setExpandedClauses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clauseRef)) {
        newSet.delete(clauseRef);
      } else {
        newSet.add(clauseRef);
      }
      return newSet;
    });
  };

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'critical':
        return 'text-red-700 dark:text-red-300 bg-red-200 dark:bg-red-900/50';
      case 'high':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
      case 'medium':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'low':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800';
    }
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'critical':
        return <AlertOctagon className="w-4 h-4" />;
      case 'high':
        return <AlertTriangle className="w-4 h-4" />;
      case 'medium':
        return <AlertCircle className="w-4 h-4" />;
      case 'low':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getClauseRiskLevel = (clause: DetailedClauseJSON): string => {
    if (clause.problematic_subclauses?.length > 0) {
      // Critical must be checked first, or the most severe clauses fall through
      // to 'low' and read as the safest thing on the page.
      const hasCritical = clause.problematic_subclauses.some(s => s.risk_level === 'Critical');
      if (hasCritical) return 'critical';
      const hasHigh = clause.problematic_subclauses.some(s => s.risk_level === 'High');
      if (hasHigh) return 'high';
      const hasMedium = clause.problematic_subclauses.some(s => s.risk_level === 'Medium');
      if (hasMedium) return 'medium';
    }
    return 'low';
  };

  const handleQuickAction = async (clauseRef: string, action: ClauseDecisionStatus) => {
    await updateClauseDecision(clauseRef, { status: action });
  };

  // Handle showing the model selection modal for initial analysis
  const handleShowModelSelection = useCallback(() => {
    setPendingReanalyze(false);
    setShowModelSelection(true);
  }, []);

  // Handle showing the model selection modal for re-analysis
  const handleShowReanalyzeModelSelection = useCallback(() => {
    setPendingReanalyze(true);
    setShowModelSelection(true);
  }, []);

  // Handle model selection and start analysis
  const handleModelSelect = useCallback(async (selection: SelectedModel) => {
    setShowModelSelection(false);

    if (pendingReanalyze) {
      setIsReanalyzing(true);
      setAnalysisProgress({ stage: 'extracting', percentage: 0, message: `Starting new analysis with ${selection.modelName}...` });

      try {
        await runAnalysis((progress) => {
          setAnalysisProgress(progress);
        }, true, selection.modelId, selection.provider);
      } catch (error) {
        console.error('Re-analysis failed:', error);
      } finally {
        setIsReanalyzing(false);
      }
    } else {
      setIsAnalyzing(true);
      setAnalysisProgress({ stage: 'extracting', percentage: 0, message: `Starting analysis with ${selection.modelName}...` });

      try {
        await runAnalysis((progress) => {
          setAnalysisProgress(progress);
        }, false, selection.modelId, selection.provider);
      } catch (error) {
        console.error('Analysis failed:', error);
      } finally {
        setIsAnalyzing(false);
      }
    }
  }, [runAnalysis, pendingReanalyze]);

  // Handle version selection
  const handleVersionSelect = useCallback(async (version: number) => {
    setShowVersionDropdown(false);
    if (version !== analysisVersion) {
      await selectAnalysisVersion(version);
    }
  }, [analysisVersion, selectAnalysisVersion]);

  // Format date for display
  const formatVersionDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get stage icon
  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'extracting':
        return <FileSearch className="w-6 h-6" />;
      case 'sectioning':
        return <Layers className="w-6 h-6" />;
      case 'analyzing':
        return <Brain className="w-6 h-6" />;
      case 'synthesizing':
        return <Sparkles className="w-6 h-6" />;
      case 'complete':
        return <CheckCircle className="w-6 h-6" />;
      default:
        return <Loader2 className="w-6 h-6 animate-spin" />;
    }
  };

  const filteredClauses = analysis?.clauses?.filter(clause => {
    if (filterRisk === 'all') return true;
    return getClauseRiskLevel(clause).toLowerCase() === filterRisk.toLowerCase();
  }) || [];

  // No analysis yet - show run analysis UI
  if (!analysis && !isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              AI Contract Analysis
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Run AI analysis to identify risks and negotiation opportunities
            </p>
          </div>
        </div>

        {/* Error Message */}
        {context.error && !isAnalyzing && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-red-800 dark:text-red-300">Analysis Failed</h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">{context.error}</p>
              </div>
            </div>
          </div>
        )}

        <WorkflowCard>
          {isAnalyzing && analysisProgress ? (
            // Progress UI during analysis
            <div className="py-8 px-4">
              <div className="max-w-md mx-auto">
                {/* Progress Icon */}
                <div className="flex justify-center mb-6">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    analysisProgress.stage === 'complete'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : analysisProgress.stage === 'error'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      : 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                  }`}>
                    {analysisProgress.stage === 'error' ? (
                      <AlertCircle className="w-8 h-8" />
                    ) : (
                      <div className="animate-pulse">
                        {getStageIcon(analysisProgress.stage)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress Message */}
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                  {analysisProgress.stage === 'complete'
                    ? 'Analysis Complete!'
                    : analysisProgress.stage === 'error'
                    ? 'Analysis Failed'
                    : 'Analyzing Contract'}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
                  {analysisProgress.message}
                </p>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
                    <span>Progress</span>
                    <span>{analysisProgress.percentage}%</span>
                  </div>
                  <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        analysisProgress.stage === 'complete'
                          ? 'bg-green-500'
                          : analysisProgress.stage === 'error'
                          ? 'bg-red-500'
                          : 'bg-primary-500'
                      }`}
                      style={{ width: `${analysisProgress.percentage}%` }}
                    />
                  </div>
                </div>

                {/* Section Progress */}
                {analysisProgress.currentSection && analysisProgress.totalSections && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    Section {analysisProgress.currentSection} of {analysisProgress.totalSections}
                    {analysisProgress.sectionTitle && (
                      <span className="block text-xs mt-1 text-gray-400 dark:text-gray-500">
                        {analysisProgress.sectionTitle}
                      </span>
                    )}
                  </div>
                )}

                {/* Elapsed Time Counter */}
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  <span>Time elapsed: <span className="font-mono font-medium">{formatElapsedTime(elapsedTime)}</span></span>
                </div>

                {/* Stage Timeline */}
                <div className="mt-8 flex items-center justify-between">
                  {[
                    { id: 'sectioning', label: 'Split' },
                    { id: 'analyzing', label: 'Analyze' },
                    { id: 'synthesizing', label: 'Synthesize' },
                    { id: 'complete', label: 'Done' }
                  ].map((stage, idx) => {
                    const stages = ['sectioning', 'analyzing', 'synthesizing', 'complete'];
                    const currentIdx = stages.indexOf(analysisProgress.stage);
                    const stageIdx = stages.indexOf(stage.id);
                    const isComplete = stageIdx < currentIdx;
                    const isCurrent = stage.id === analysisProgress.stage;

                    return (
                      <div key={stage.id} className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                          isComplete
                            ? 'bg-green-500 text-white'
                            : isCurrent
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          {isComplete ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {stage.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            // Initial state - Ready to analyze
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Brain className="w-10 h-10 text-primary-600 dark:text-primary-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Ready to Analyze
              </h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
                Our AI will analyze the contract section-by-section to identify key clauses,
                potential risks, and negotiation opportunities.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                Make sure you have uploaded a contract document in the Overview step.
              </p>
              <button
                onClick={handleShowModelSelection}
                disabled={isSaving || isAnalyzing}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-lg transition-colors"
              >
                <Brain className="w-5 h-5" />
                Run AI Analysis
              </button>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
                Uses multi-pass analysis for comprehensive results
              </p>
            </div>
          )}
        </WorkflowCard>

        <div className="flex justify-between">
          <button
            onClick={handlePrevStep}
            disabled={isAnalyzing}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Overview
          </button>
        </div>

        {/* Model Selection Modal */}
        <ModelSelectionModal
          isOpen={showModelSelection}
          onClose={() => setShowModelSelection(false)}
          onSelectModel={handleModelSelect}
          isLoading={isAnalyzing || isReanalyzing}
        />
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const highRiskCount = analysis?.clauses?.filter(c => getClauseRiskLevel(c) === 'high').length || 0;
  const mediumRiskCount = analysis?.clauses?.filter(c => getClauseRiskLevel(c) === 'medium').length || 0;
  const lowRiskCount = analysis?.clauses?.filter(c => getClauseRiskLevel(c) === 'low').length || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              AI Analysis Results
            </h1>
            {/* Version Selector */}
            {analysisVersions && analysisVersions.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                >
                  <History className="w-4 h-4" />
                  Version {analysisVersion}
                  <ChevronDown className={`w-4 h-4 transition-transform ${showVersionDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showVersionDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-10">
                    <div className="py-1">
                      {analysisVersions.map((v) => (
                        <button
                          key={v._id}
                          onClick={() => handleVersionSelect(v.version)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-800 ${
                            v.version === analysisVersion
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">Version {v.version}</span>
                            {v.version === analysisVersion && (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatVersionDate(v.createdAt)}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {analysis?.clauses?.length || 0} clauses analyzed
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Re-analyze Button */}
          <button
            onClick={handleShowReanalyzeModelSelection}
            disabled={isReanalyzing || isSaving}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50 rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isReanalyzing ? 'animate-spin' : ''}`} />
            Re-analyze
          </button>
          <button
            onClick={handleNextStep}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            Proceed to Review
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Re-analysis Progress */}
      {isReanalyzing && analysisProgress && (
        <div className="p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-primary-700 dark:text-primary-300">
                {analysisProgress.message}
              </p>
              <div className="mt-2 w-full h-2 bg-primary-200 dark:bg-primary-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-600 dark:bg-primary-400 rounded-full transition-all duration-300"
                  style={{ width: `${analysisProgress.percentage}%` }}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400">
                <Clock className="w-4 h-4" />
                <span className="font-mono">{formatElapsedTime(elapsedTime)}</span>
              </div>
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                {analysisProgress.percentage}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Executive Summary */}
      {analysis?.executive_summary && (
        <WorkflowCard
          title="Executive Summary"
          icon={<Brain className="w-5 h-5" />}
        >
          <div className="space-y-4">
            <p className="text-gray-700 dark:text-gray-300">
              {analysis.executive_summary.overview}
            </p>

            {/* Favorability Score */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                Overall Favorability:
              </span>
              <div className="flex items-center gap-2">
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${
                      analysis.executive_summary.overall_favorability_score >= 7
                        ? 'bg-green-500'
                        : analysis.executive_summary.overall_favorability_score >= 4
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${analysis.executive_summary.overall_favorability_score * 10}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                  {analysis.executive_summary.overall_favorability_score}/10
                </span>
              </div>
            </div>

            {/* Critical Issues */}
            {analysis.executive_summary.critical_issues && analysis.executive_summary.critical_issues.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-border">
                <h4 className="text-sm font-semibold text-red-600 dark:text-red-400 mb-3">
                  Critical Issues ({analysis.executive_summary.critical_issues.length})
                </h4>
                <div className="space-y-2">
                  {analysis.executive_summary.critical_issues.map((issue, index) => (
                    <div
                      key={index}
                      className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                    >
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-red-700 dark:text-red-400">
                            {issue.title}
                          </p>
                          <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                            {issue.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </WorkflowCard>
      )}

      {/* Risk Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => setFilterRisk(filterRisk === 'high' ? 'all' : 'high')}
          className={`p-4 rounded-xl border transition-all ${
            filterRisk === 'high'
              ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
              : 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface hover:border-red-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400">High Risk</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{highRiskCount}</p>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setFilterRisk(filterRisk === 'medium' ? 'all' : 'medium')}
          className={`p-4 rounded-xl border transition-all ${
            filterRisk === 'medium'
              ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
              : 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface hover:border-yellow-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400">Medium Risk</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{mediumRiskCount}</p>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setFilterRisk(filterRisk === 'low' ? 'all' : 'low')}
          className={`p-4 rounded-xl border transition-all ${
            filterRisk === 'low'
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-200 dark:border-dark-border bg-white dark:bg-dark-surface hover:border-green-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400">Low Risk</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{lowRiskCount}</p>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Framework review. Placed above the clause list so a reviewer sees the
          benchmark deviations and cross-clause traps before working through
          clauses one at a time - the traps are invisible clause by clause. */}
      {analysis?.playbookReview && (
        <WorkflowCard
          title={analysis.playbookReview.playbookName}
          subtitle={`${analysis.playbookReview.perspective} \u00b7 Framework v${analysis.playbookReview.playbookVersion}`}
        >
          <PlaybookReviewPanel review={analysis.playbookReview} compact />
        </WorkflowCard>
      )}

      {/* Finance rule book. Rendered only when at least one rule applied. */}
      {analysis?.financeRuleReview && (
        <WorkflowCard
          title="Finance Rule Book"
          subtitle={`${analysis.financeRuleReview.rulesChecked} rule${analysis.financeRuleReview.rulesChecked === 1 ? '' : 's'} checked \u00b7 ${analysis.financeRuleReview.counts.VIOLATED} violated`}
        >
          <FinanceRuleReviewPanel review={analysis.financeRuleReview} />
        </WorkflowCard>
      )}

      {/* Clauses List */}
      <WorkflowCard
        title={`Clause Analysis ${filterRisk !== 'all' ? `(${filterRisk} risk)` : ''}`}
        actions={
          filterRisk !== 'all' && (
            <button
              onClick={() => setFilterRisk('all')}
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Clear filter
            </button>
          )
        }
      >
        <div className="space-y-4">
          {filteredClauses.map((clause, index) => {
            const clauseRef = clause.reference || `clause-${index}`;
            const uniqueKey = `${clauseRef}-${index}`; // Ensure unique key even if clauseRef is duplicated
            const isExpanded = expandedClauses.has(clauseRef);
            const riskLevel = getClauseRiskLevel(clause);
            const decision = clauseDecisions[clauseRef];

            return (
              <div
                key={uniqueKey}
                className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden"
              >
                {/* Clause Header */}
                <button
                  onClick={() => toggleClause(clauseRef)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getRiskColor(riskLevel)}`}>
                      {getRiskIcon(riskLevel)}
                      {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}
                    </span>
                    <div className="text-left">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {clause.reference || `Clause ${index + 1}`}{clause.title ? ` - ${clause.title}` : ''}
                      </p>
                      {clause.summary && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                          {clause.summary}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {decision?.status && decision.status !== 'pending' && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        decision.status === 'approved'
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : decision.status === 'rejected'
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                      }`}>
                        {decision.status}
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="p-4 pt-0 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/30">
                    {/* Party Obligations */}
                    {clause.party_obligations && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Party Obligations
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {clause.party_obligations}
                        </p>
                      </div>
                    )}

                    {/* Problematic Subclauses */}
                    {clause.problematic_subclauses && clause.problematic_subclauses.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Issues Identified
                        </h4>
                        <div className="space-y-2">
                          {clause.problematic_subclauses.map((subclause, idx) => (
                            <div
                              key={idx}
                              className={`p-3 rounded-lg ${getRiskColor(subclause.risk_level)}`}
                            >
                              <div className="flex items-start gap-2">
                                {getRiskIcon(subclause.risk_level)}
                                <div>
                                  <p className="font-medium text-sm">
                                    {subclause.subref}: {subclause.issue}
                                  </p>
                                  <p className="text-xs mt-1 opacity-80">
                                    {subclause.note}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Negotiation Analysis */}
                    {clause.negotiation_analysis && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Negotiation Analysis
                        </h4>
                        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          <p><strong>Favorability:</strong> {clause.negotiation_analysis.favorability}</p>
                          <p><strong>Market Standard:</strong> {clause.negotiation_analysis.market_standard}</p>
                          {clause.negotiation_analysis.recommended_modification && (
                            <p><strong>Recommendation:</strong> {clause.negotiation_analysis.recommended_modification}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200 dark:border-dark-border">
                      <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Quick Actions:</span>
                      <button
                        onClick={() => handleQuickAction(clauseRef, 'approved')}
                        className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          decision?.status === 'approved'
                            ? 'bg-green-600 text-white'
                            : 'text-green-600 hover:bg-green-100 dark:hover:bg-green-900/30'
                        }`}
                      >
                        <ThumbsUp className="w-4 h-4" />
                        Accept
                      </button>
                      <button
                        onClick={() => handleQuickAction(clauseRef, 'flagged')}
                        className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          decision?.status === 'flagged'
                            ? 'bg-yellow-600 text-white'
                            : 'text-yellow-600 hover:bg-yellow-100 dark:hover:bg-yellow-900/30'
                        }`}
                      >
                        <Flag className="w-4 h-4" />
                        Flag
                      </button>
                      <button
                        onClick={() => handleQuickAction(clauseRef, 'rejected')}
                        className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          decision?.status === 'rejected'
                            ? 'bg-red-600 text-white'
                            : 'text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30'
                        }`}
                      >
                        <ThumbsDown className="w-4 h-4" />
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {filteredClauses.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No clauses match the current filter
            </div>
          )}
        </div>
      </WorkflowCard>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={handlePrevStep}
          className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Overview
        </button>
        <button
          onClick={handleNextStep}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
        >
          Continue to Clause Review
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Model Selection Modal */}
      <ModelSelectionModal
        isOpen={showModelSelection}
        onClose={() => setShowModelSelection(false)}
        onSelectModel={handleModelSelect}
        isLoading={isAnalyzing || isReanalyzing}
      />
    </div>
  );
};
